import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Bell, Shield, Eye, Moon, Sun, Smartphone } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const Settings = () => {
	// Notification states
	const [notifyUpdates, setNotifyUpdates] = useState(true);
	const [notifyDeadlines, setNotifyDeadlines] = useState(true);
	const [notifyAssignments, setNotifyAssignments] = useState(true);
	const [notifyMFOChanges, setNotifyMFOChanges] = useState(true);

	// Appearance states
	const [darkMode, setDarkMode] = useState(false);
	const [compactView, setCompactView] = useState(false);

	// Security states
	const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
	const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
	const [deviceManagementDialogOpen, setDeviceManagementDialogOpen] =
		useState(false);

	// Password change form
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

	// Device management
	const [devices, setDevices] = useState<any[]>([]);
	const [loadingDevices, setLoadingDevices] = useState(false);

	const { user } = useAuth();

	// Load saved settings on component mount
	useEffect(() => {
		const loadSettings = () => {
			const savedSettings = localStorage.getItem("userSettings");
			if (savedSettings) {
				const settings = JSON.parse(savedSettings);
				// Load notification settings
				setNotifyUpdates(settings.notifyUpdates ?? true);
				setNotifyDeadlines(settings.notifyDeadlines ?? true);
				setNotifyAssignments(settings.notifyAssignments ?? true);
				setNotifyMFOChanges(settings.notifyMFOChanges ?? true);

				// Load appearance settings
				setDarkMode(settings.darkMode ?? false);
				setCompactView(settings.compactView ?? false);
			}
		};

		loadSettings();
	}, []);

	// Apply dark mode changes
	useEffect(() => {
		if (darkMode) {
			document.documentElement.classList.add("dark");
		} else {
			document.documentElement.classList.remove("dark");
		}
	}, [darkMode]);

	// Apply compact view changes
	useEffect(() => {
		if (compactView) {
			document.documentElement.classList.add("compact");
		} else {
			document.documentElement.classList.remove("compact");
		}
	}, [compactView]);

	const handleSaveSettings = () => {
		// Save settings to localStorage
		const settings = {
			notifyUpdates,
			notifyDeadlines,
			notifyAssignments,
			notifyMFOChanges,
			darkMode,
			compactView,
		};

		localStorage.setItem("userSettings", JSON.stringify(settings));

		// If user is logged in, we could also save to database here
		if (user) {
			// For future implementation: Save to user profile in database
		}

		toast({
			title: "Settings saved",
			description: "Your preferences have been updated successfully",
		});
	};

	const handleChangePassword = async () => {
		setPasswordError("");

		if (!currentPassword || !newPassword || !confirmPassword) {
			setPasswordError("All fields are required");
			return;
		}

		if (newPassword !== confirmPassword) {
			setPasswordError("New passwords don't match");
			return;
		}

		if (newPassword.length < 6) {
			setPasswordError("Password must be at least 6 characters");
			return;
		}

		setIsSubmittingPassword(true);

		try {
			const { error } = await supabase.auth.updateUser({
				password: newPassword,
			});

			if (error) throw error;

			toast({
				title: "Password updated",
				description: "Your password has been changed successfully",
			});

			setPasswordDialogOpen(false);
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
		} catch (error: any) {
			setPasswordError(error.message || "Failed to update password");
		} finally {
			setIsSubmittingPassword(false);
		}
	};

	const fetchUserDevices = async () => {
		if (!user) return;

		setLoadingDevices(true);

		try {
			// This is a placeholder - Supabase doesn't directly expose active sessions
			// In a real implementation, you would fetch this from your backend
			const mockDevices = [
				{
					id: "1",
					device: "Chrome on Windows",
					lastActive: new Date().toISOString(),
					current: true,
				},
				{
					id: "2",
					device: "Mobile App on iPhone",
					lastActive: new Date(Date.now() - 86400000).toISOString(),
					current: false,
				},
			];

			setDevices(mockDevices);
		} catch (error) {
			console.error("Error fetching devices:", error);
			toast({
				title: "Error",
				description: "Failed to load devices",
				variant: "destructive",
			});
		} finally {
			setLoadingDevices(false);
		}
	};

	const handleSignOutDevice = async (deviceId: string) => {
		try {
			// This is a placeholder - in a real implementation you would call your backend
			// to invalidate the specific session
			toast({
				title: "Device signed out",
				description: "The device has been signed out successfully",
			});

			// Remove the device from the list
			setDevices(devices.filter((device) => device.id !== deviceId));
		} catch (error) {
			toast({
				title: "Error",
				description: "Failed to sign out device",
				variant: "destructive",
			});
		}
	};

	const handleSignOutAllDevices = async () => {
		try {
			// In a real implementation this would call your backend
			// For Supabase we can use signOut
			const { error } = await supabase.auth.signOut();
			if (error) throw error;

			toast({
				title: "All devices signed out",
				description: "You have been signed out of all devices",
			});

			// Redirect to login page after signing out
			window.location.href = "/login";
		} catch (error) {
			toast({
				title: "Error",
				description: "Failed to sign out all devices",
				variant: "destructive",
			});
		}
	};

	const openDeviceManagement = () => {
		fetchUserDevices();
		setDeviceManagementDialogOpen(true);
	};

	return (
		<div className='space-y-6'>
			<div>
				<h2 className='text-3xl font-bold tracking-tight'>Settings</h2>
				<p className='text-muted-foreground'>
					Manage your application settings and preferences
				</p>
			</div>

			<Tabs
				defaultValue='notifications'
				className='space-y-4'>
				<TabsList>
					<TabsTrigger value='notifications'>
						<Bell className='h-4 w-4 mr-2' />
						Notifications
					</TabsTrigger>
					<TabsTrigger value='appearance'>
						<Eye className='h-4 w-4 mr-2' />
						Appearance
					</TabsTrigger>
					<TabsTrigger value='security'>
						<Shield className='h-4 w-4 mr-2' />
						Security
					</TabsTrigger>
				</TabsList>

				<TabsContent
					value='notifications'
					className='space-y-4'>
					<Card>
						<CardHeader>
							<CardTitle>Notification Preferences</CardTitle>
							<CardDescription>
								Configure how you receive notifications from the application
							</CardDescription>
						</CardHeader>
						<CardContent className='space-y-4'>
							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label htmlFor='notify-updates'>System Updates</Label>
									<p className='text-sm text-muted-foreground'>
										Receive notifications about system updates and announcements
									</p>
								</div>
								<Switch
									id='notify-updates'
									checked={notifyUpdates}
									onCheckedChange={setNotifyUpdates}
								/>
							</div>

							<Separator />

							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label htmlFor='notify-deadlines'>Deadline Reminders</Label>
									<p className='text-sm text-muted-foreground'>
										Get notified about approaching MFO and task deadlines
									</p>
								</div>
								<Switch
									id='notify-deadlines'
									checked={notifyDeadlines}
									onCheckedChange={setNotifyDeadlines}
								/>
							</div>

							<Separator />

							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label htmlFor='notify-assignments'>Task Assignments</Label>
									<p className='text-sm text-muted-foreground'>
										Receive notifications when you're assigned to new tasks or MFOs
									</p>
								</div>
								<Switch
									id='notify-assignments'
									checked={notifyAssignments}
									onCheckedChange={setNotifyAssignments}
								/>
							</div>

							<Separator />

							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label htmlFor='notify-mfo-changes'>MFO Updates</Label>
									<p className='text-sm text-muted-foreground'>
										Get notified when MFOs you're assigned to are updated
									</p>
								</div>
								<Switch
									id='notify-mfo-changes'
									checked={notifyMFOChanges}
									onCheckedChange={setNotifyMFOChanges}
								/>
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent
					value='appearance'
					className='space-y-4'>
					<Card>
						<CardHeader>
							<CardTitle>Appearance Settings</CardTitle>
							<CardDescription>
								Customize how the application looks and behaves
							</CardDescription>
						</CardHeader>
						<CardContent className='space-y-4'>
							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label htmlFor='dark-mode'>Dark Mode</Label>
									<p className='text-sm text-muted-foreground'>
										Switch between light and dark theme
									</p>
								</div>
								<div className='flex items-center space-x-2'>
									<Sun className='h-4 w-4 text-muted-foreground' />
									<Switch
										id='dark-mode'
										checked={darkMode}
										onCheckedChange={setDarkMode}
									/>
									<Moon className='h-4 w-4 text-muted-foreground' />
								</div>
							</div>

							<Separator />

							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label htmlFor='compact-view'>Compact View</Label>
									<p className='text-sm text-muted-foreground'>
										Show more content with reduced spacing
									</p>
								</div>
								<Switch
									id='compact-view'
									checked={compactView}
									onCheckedChange={setCompactView}
								/>
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent
					value='security'
					className='space-y-4'>
					<Card>
						<CardHeader>
							<CardTitle>Security Settings</CardTitle>
							<CardDescription>
								Manage your account security preferences
							</CardDescription>
						</CardHeader>
						<CardContent className='space-y-4'>
							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label>Two-Factor Authentication</Label>
									<p className='text-sm text-muted-foreground'>
										Secure your account with two-factor authentication
									</p>
								</div>
								<Button
									variant='outline'
									onClick={() => setTwoFactorDialogOpen(true)}>
									Set Up 2FA
								</Button>
							</div>

							<Separator />

							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label>Password</Label>
									<p className='text-sm text-muted-foreground'>
										Change your account password
									</p>
								</div>
								<Button
									variant='outline'
									onClick={() => setPasswordDialogOpen(true)}>
									Change Password
								</Button>
							</div>

							<Separator />

							<div className='flex items-center justify-between'>
								<div className='space-y-0.5'>
									<Label>Session Management</Label>
									<p className='text-sm text-muted-foreground'>
										Manage your active sessions and devices
									</p>
								</div>
								<Button
									variant='outline'
									onClick={openDeviceManagement}>
									<Smartphone className='h-4 w-4 mr-2' />
									Manage Devices
								</Button>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			<div className='flex justify-end'>
				<Button onClick={handleSaveSettings}>Save Settings</Button>
			</div>

			{/* Password Change Dialog */}
			<Dialog
				open={passwordDialogOpen}
				onOpenChange={setPasswordDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Change Password</DialogTitle>
						<DialogDescription>
							Update your account password. Make sure it's secure.
						</DialogDescription>
					</DialogHeader>
					<div className='space-y-4 py-4'>
						<div className='space-y-2'>
							<Label htmlFor='current-password'>Current Password</Label>
							<Input
								id='current-password'
								type='password'
								value={currentPassword}
								onChange={(e) => setCurrentPassword(e.target.value)}
							/>
						</div>
						<div className='space-y-2'>
							<Label htmlFor='new-password'>New Password</Label>
							<Input
								id='new-password'
								type='password'
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
							/>
						</div>
						<div className='space-y-2'>
							<Label htmlFor='confirm-password'>Confirm New Password</Label>
							<Input
								id='confirm-password'
								type='password'
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
							/>
						</div>
						{passwordError && (
							<p className='text-sm text-destructive'>{passwordError}</p>
						)}
					</div>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setPasswordDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleChangePassword}
							disabled={isSubmittingPassword}>
							{isSubmittingPassword ? "Updating..." : "Update Password"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Two-Factor Authentication Dialog */}
			<Dialog
				open={twoFactorDialogOpen}
				onOpenChange={setTwoFactorDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Two-Factor Authentication</DialogTitle>
						<DialogDescription>
							Enhance your account security with two-factor authentication.
						</DialogDescription>
					</DialogHeader>
					<div className='space-y-4 py-4'>
						<p className='text-sm text-foreground'>
							Two-factor authentication adds an extra layer of security to your account
							by requiring a code from your mobile device in addition to your password.
						</p>
						<div className='rounded-lg bg-muted p-4'>
							<p className='text-sm font-medium'>Currently unavailable</p>
							<p className='text-sm text-muted-foreground mt-1'>
								This feature is coming soon. Francis Tin-ao is currently working on it
								to secure your account.
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setTwoFactorDialogOpen(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Device Management Dialog */}
			<Dialog
				open={deviceManagementDialogOpen}
				onOpenChange={setDeviceManagementDialogOpen}>
				<DialogContent className='sm:max-w-md'>
					<DialogHeader>
						<DialogTitle>Manage Devices</DialogTitle>
						<DialogDescription>
							View and manage devices that are signed in to your account.
						</DialogDescription>
					</DialogHeader>
					<div className='space-y-4 py-4 max-h-[300px] overflow-y-auto'>
						{loadingDevices ? (
							<p className='text-center text-sm text-muted-foreground'>
								Loading devices...
							</p>
						) : devices.length === 0 ? (
							<p className='text-center text-sm text-muted-foreground'>
								No active devices found
							</p>
						) : (
							<div className='space-y-4'>
								{devices.map((device) => (
									<div
										key={device.id}
										className='flex justify-between items-center p-3 border rounded-lg'>
										<div>
											<p className='font-medium text-sm flex items-center'>
												{device.device}
												{device.current && (
													<span className='ml-2 px-1.5 py-0.5 rounded text-xs bg-primary text-primary-foreground'>
														Current
													</span>
												)}
											</p>
											<p className='text-xs text-muted-foreground'>
												Last active: {new Date(device.lastActive).toLocaleString()}
											</p>
										</div>
										{!device.current && (
											<Button
												variant='ghost'
												size='sm'
												onClick={() => handleSignOutDevice(device.id)}>
												Sign Out
											</Button>
										)}
									</div>
								))}
							</div>
						)}
					</div>
					<DialogFooter className='flex flex-col-reverse sm:flex-row sm:justify-between sm:space-x-2'>
						<Button
							variant='outline'
							onClick={() => setDeviceManagementDialogOpen(false)}>
							Close
						</Button>
						<Button
							variant='destructive'
							onClick={handleSignOutAllDevices}>
							Sign Out All Devices
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default Settings;
